import { useEffect, useState } from "react"
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "../components/ui/command"
import type { RendererProps } from "../core/element-registry"
import type { CogoElement } from "../core/types"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"
import { liftV4Props } from "./element-renderer"

type IndexedCommandItem = {
  element: CogoElement
  index: number
  label: string
  value: string
}

function resolvedAuthoredValue(
  value: unknown,
  resolveSymbol: (value: string) => unknown,
): unknown {
  return typeof value === "string" ? resolveSymbol(value) : value
}

function selectedIndexValue(value: unknown, itemCount: number): number {
  const numeric = Number(value)
  return Number.isInteger(numeric) && numeric >= 0 && numeric < itemCount
    ? numeric
    : -1
}

export function CommandRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()

  const children = element.children ?? []
  // JITRULE Task 2: the found trigger/input child may nest its fields
  // under `props`.
  const rawSearchInput = children.find((c) => c.type === "input")
  const searchInput = rawSearchInput ? liftV4Props(rawSearchInput) : undefined
  const groups = children.filter((c) => c.type !== "input")

  const placeholder = searchInput?.placeholder
    ? String(resolveSymbol(searchInput.placeholder) ?? "")
    : "Type a command or search..."

  let nextIndex = 0
  const indexedGroups = groups.map((group) => {
    // JITRULE Task 2: group children may nest their fields under `props`.
    const groupItems = (group.children ?? []).map(liftV4Props)
    const label = groupItems.find((item) => item.type === "text")
    const heading = label
      ? String(resolveSymbol(label.text ?? "") ?? "")
      : undefined
    const items: IndexedCommandItem[] = groupItems
      .filter((item) => item.type !== "text")
      .map((item) => {
        const itemLabel = item.text
          ? String(resolveSymbol(item.text) ?? "")
          : item.id
        const itemValue = String(item.value ?? item.variant ?? itemLabel)
        return {
          element: item,
          index: nextIndex++,
          label: itemLabel,
          value: itemValue,
        }
      })
    return { element: group, heading, items }
  })
  const commandItems = indexedGroups.flatMap((group) => group.items)
  const elementRecord = element as unknown as Record<string, unknown>
  const authoredSearchQuery = String(
    resolvedAuthoredValue(elementRecord.searchQuery, resolveSymbol) ?? "",
  )
  const authoredSelectedIndex = selectedIndexValue(
    resolvedAuthoredValue(elementRecord.selectedIndex, resolveSymbol),
    commandItems.length,
  )
  const [searchQuery, setSearchQuery] = useState(authoredSearchQuery)
  const [selectedIndex, setSelectedIndex] = useState(authoredSelectedIndex)

  useEffect(() => {
    setSearchQuery(authoredSearchQuery)
  }, [authoredSearchQuery])

  useEffect(() => {
    setSelectedIndex(authoredSelectedIndex)
    const item = commandItems[authoredSelectedIndex]
    dispatchEvent(element.id, "selectionChange", {
      index: authoredSelectedIndex,
      selectedIndex: authoredSelectedIndex,
      value: item?.value ?? null,
    })
  }, [authoredSelectedIndex, element.id, dispatchEvent])

  const selectIndex = (index: number) => {
    const item = commandItems[index]
    if (!item) return
    setSelectedIndex(index)
    dispatchEvent(element.id, "selectionChange", {
      index,
      selectedIndex: index,
      value: item.value,
    })
  }

  const activateItem = (item: IndexedCommandItem) => {
    selectIndex(item.index)
    dispatchEvent(item.element.id, "tap", {
      value: item.value,
      index: item.index,
      selectedIndex: item.index,
    })
  }

  const visibleItems = commandItems.filter((item) => {
    const query = searchQuery.trim().toLowerCase()
    return !query || item.label.toLowerCase().includes(query)
  })
  const updateSearchQuery = (value: string) => {
    setSearchQuery(value)
    if (searchInput) {
      dispatchEvent(searchInput.id, "change", {
        value,
        query: value,
        searchQuery: value,
      })
    }
  }

  return (
    <Command
      className={cn(element.className)}
      data-testid={`command-${element.id}`}
      data-search-query={searchQuery}
      data-selected-index={selectedIndex}
      aria-label={element.ariaLabel ?? "Command palette"}
      value={commandItems[selectedIndex]?.value ?? ""}
    >
      <CommandInput
        placeholder={placeholder}
        aria-label={searchInput?.ariaLabel ?? "Search commands"}
        value={searchQuery}
        onValueChange={updateSearchQuery}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            const item = visibleItems.find((candidate) =>
              candidate.label.toLowerCase() === searchQuery.trim().toLowerCase()
            ) ?? visibleItems[0]
            if (item) {
              event.preventDefault()
              activateItem(item)
            }
          }
        }}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {indexedGroups.map((group) => (
            <CommandGroup key={group.element.id} heading={group.heading}>
              {group.items.map((item) => (
                <CommandItem
                  key={item.element.id}
                  value={item.value}
                  data-cogo-element-id={item.element.id}
                  data-cogo-type={item.element.type}
                  data-selected={selectedIndex === item.index ? "true" : "false"}
                  aria-selected={selectedIndex === item.index}
                  onSelect={() => activateItem(item)}
                  aria-label={item.element.ariaLabel ?? item.label}
                >
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
        ))}
      </CommandList>
    </Command>
  )
}
